import React, { useState, useRef, useCallback } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { questionShape } from '../../../../../propsShape';
import { H4, Text, NewButton, Alert, Icon } from '../../../../../components';
import { LexicalCodeBlock } from '../../molecules';
import { useSqlEditorState } from '../../../hooks';
import { formatSql } from '../../../utils';

const SourceViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 16px;
  row-gap: 12px;
`;

// Styled components remain as defined
const AssetSourceViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background: var(--elevation-high);
`;

const LexicalCodeBlockContainer = styled.div`
  padding: 12px;
  border: ${(props) =>
    props.isEditing ? '1px solid var(--primary-default)' : '1px solid var(--border-muted)'};
  border-radius: 0px 0px 8px 8px;
`;

const LexicalCodeBlockHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid var(--border-muted);
  border-radius: 8px 8px 0px 0px;

  border-bottom: none;
  background: ${(props) =>
    props.isEditing ? 'var(--wash-orange-default)' : 'var(--wash-gray-default)'};
  padding: 12px;
`;

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// --- Main Component ---
const AssetSourceView = ({
  question,
  handleCreateAnswer = null,
  isEditSqlError,
  setIsEditSqlError
}) => {
  const { formattedSql, setFormattedSql, currentSqlRef, originalSqlRef } = useSqlEditorState(
    question.answer?.rawSql,
    question.answer?.id
  );
  const [isEditing, setIsEditing] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const editorRef = useRef(null);

  // A function that transforms the pretty printed SQL into the original one-line format.
  const restoreOriginalFormat = (sql) =>
    // Replace one or more whitespace characters (including newlines) with a single space.
    sql.replace(/\s+/g, ' ').trim();
  const handleRunClick = useCallback(async () => {
    if (currentSqlRef.current) {
      // Convert the user-edited, pretty-printed SQL into the original format.
      const finalSql = restoreOriginalFormat(currentSqlRef.current.trimStart());
      const retVal = await handleCreateAnswer(question, finalSql);
      setIsEditSqlError(!retVal);
      // Update the stored version with the transformed SQL.
      originalSqlRef.current = finalSql;
      setFormattedSql(finalSql);
      setHasUnsavedChanges(false);
    }
  }, [handleCreateAnswer, question, setIsEditSqlError]);

  // Reset the editor to the original formatted SQL.
  const handleReset = useCallback(() => {
    const formatted = formatSql(question.answer?.rawSql);
    setFormattedSql(formatted);
    currentSqlRef.current = formatted;
    originalSqlRef.current = formatted;
    setHasUnsavedChanges(false);
    setIsEditing(false);
    setIsEditSqlError(false);
  }, [question.answer, setFormattedSql, setIsEditSqlError]);

  const onSqlEditClick = useCallback(() => {
    setIsEditing((prev) => !prev);
    setIsEditSqlError(false);
  }, [setIsEditSqlError]);

  return (
    <SourceViewContainer>
      {isEditSqlError && (
        <Alert
          isError={isEditSqlError}
          backgroundColor='var(--wash-red-default)'
          icon={<Icon name='alert' />}
          message='Your updated SQL query failed. Please try again.'
          actions={
            <NewButton onClick={() => setIsEditSqlError(false)} tertiary>
              Dismiss
            </NewButton>
          }
        />
      )}
      <AssetSourceViewContainer>
        <LexicalCodeBlockHeader isEditing={isEditing}>
          <H4>Edit SQL Query</H4>
          {handleCreateAnswer && (
            <ActionsContainer>
              {hasUnsavedChanges ? (
                <>
                  <Text $color='var(--content-subtitle)'>Unsaved changes</Text>
                  <NewButton onClick={handleReset}>Reset</NewButton>
                  <NewButton primary onClick={handleRunClick}>
                    Save and update
                  </NewButton>
                </>
              ) : (
                <NewButton onClick={onSqlEditClick}>
                  {!isEditing && <Icon name='pencil' displaySize={20} />}
                  {isEditing ? 'Cancel' : 'Edit'}
                </NewButton>
              )}
            </ActionsContainer>
          )}
        </LexicalCodeBlockHeader>
        {formattedSql && (
          <LexicalCodeBlockContainer isEditing={isEditing}>
            <LexicalCodeBlock
              code={formattedSql}
              language='sql'
              isEditing={isEditing}
              onChange={(sql) => {
                currentSqlRef.current = sql;
                setHasUnsavedChanges(sql !== originalSqlRef.current);
              }}
              editorRef={editorRef}
            />
          </LexicalCodeBlockContainer>
        )}
      </AssetSourceViewContainer>
    </SourceViewContainer>
  );
};

AssetSourceView.propTypes = {
  question: questionShape.isRequired,
  handleCreateAnswer: PropTypes.func,
  isEditSqlError: PropTypes.bool.isRequired,
  setIsEditSqlError: PropTypes.func.isRequired
};

export { AssetSourceView };
